import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Header from "../components/app/Header";
import { KeyRound, ArrowRight, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function ChangePasswordPage() {
  const { user, changePassword, logout } = useAuth();
  const nav = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const forced = !!user?.force_password_change;

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6)
      return toast.error("New password must be at least 6 characters");
    if (next !== confirm)
      return toast.error("New password and confirm don't match");
    if (next === current)
      return toast.error("New password must differ from current password");
    setBusy(true);
    try {
      await changePassword(current, next);
      toast.success("Password updated");
      nav("/dashboard", { replace: true });
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Failed to update password"
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-xl mx-auto px-6 md:px-10 py-16">
        <div className="rounded-2xl border border-zinc-800/80 bg-zinc-950/40 p-8 grain relative overflow-hidden">
          <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-amber-400/10 blur-3xl" />
          <div className="relative">
            <div className="w-12 h-12 rounded-2xl bg-amber-500/10 border border-amber-500/40 flex items-center justify-center glow-gold mb-4">
              <KeyRound className="w-5 h-5 text-amber-300" />
            </div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-amber-300/80 mb-2">
              {forced ? "// password reset by admin" : "// security"}
            </div>
            <h1 className="font-display text-3xl md:text-4xl tracking-tighter font-black">
              {forced ? "Set a new password" : "Change your password"}
            </h1>
            <p className="text-zinc-400 text-sm mt-2">
              {forced
                ? "Your admin reset your password. Set a fresh one only you know — you'll be returned to the dashboard right after."
                : "You'll be signed out of other devices when you save."}
            </p>

            <form onSubmit={submit} className="mt-8 space-y-5">
              <Field
                label={forced ? "Temporary password (from admin)" : "Current password"}
                testid="cp-current"
                type={show ? "text" : "password"}
                value={current}
                onChange={setCurrent}
                placeholder="••••••••"
              />
              <Field
                label="New password (min 6 chars)"
                testid="cp-new"
                type={show ? "text" : "password"}
                value={next}
                onChange={setNext}
                placeholder="••••••••"
              />
              <Field
                label="Confirm new password"
                testid="cp-confirm"
                type={show ? "text" : "password"}
                value={confirm}
                onChange={setConfirm}
                placeholder="••••••••"
              />
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-300"
                  data-testid="cp-toggle-visibility"
                >
                  {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  {show ? "Hide" : "Show"} passwords
                </button>
              </div>

              <button
                type="submit"
                data-testid="cp-submit"
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl px-6 py-3.5 transition-all glow-primary disabled:opacity-60"
              >
                {busy ? "Saving…" : "Save new password"}
                <ArrowRight className="w-4 h-4" />
              </button>

              {forced && (
                <button
                  type="button"
                  onClick={logout}
                  data-testid="cp-logout"
                  className="w-full text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-rose-300 mt-2"
                >
                  Sign out instead
                </button>
              )}
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}

const Field = ({ label, testid, type, value, onChange, placeholder }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5">
      {label}
    </div>
    <input
      data-testid={testid}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-transparent border-b border-zinc-700 focus:border-emerald-500 outline-none py-2.5 text-white placeholder:text-zinc-600 transition-colors"
    />
  </div>
);
